import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Banknote, GitCompare, Loader2, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { adminFetch } from '@/hooks/admin/useAdminApi';
import { InfoDot } from '@/components/ui/InfoDot';
import { PayTabs, type PayTab } from '@/components/admin/PaymentsLayout';
import { usePaymentReturns } from '@/hooks/admin/usePaymentCutover';
import { useStripePayouts } from '@/hooks/admin/useStripePayouts';
import { usePaymentTracking } from '@/hooks/usePaymentTracking';
import { useRemittances } from '@/hooks/useRemittances';
import { useAdminCreditRequests } from '@/hooks/useCreditRequests';
import { StripePayoutsPanel, payoutNeedsLook } from './StripePayoutsPanel';
import { ReturnsPanel, returnView } from './ReturnsPanel';
import { RemittancesPanel } from './RemittancesPanel';
import { PaymentTrackingPanel, unappliedNeedingLook } from './PaymentTrackingPanel';
import { CreditRequestsPanel } from './CreditRequestsPanel';

/**
 * AdminPayments — /admin/payments: the internal payments console.
 * Stripe is the only payment rail (the Bill & Pay bridge/cutover console
 * was removed 2026-08-12 when the B&P integration was retired).
 *
 *   - Book settled payments — record ACH payments that have cleared at
 *     Stripe into QuickBooks now, instead of waiting for the hourly
 *     stripe-settlement-sweep cron.
 *   - Sync from QuickBooks — pull changed invoices (payments made outside
 *     the portal) AND the customer list (names, new customers, merges) into
 *     the QBO mirror immediately.
 *
 * Below the page-wide buttons, one tab per job (Sky, 2026-10-30: "it's a big
 * list that's hard to manage"), Stripe payouts first. Every tab is built the
 * same way from components/admin/PaymentsLayout: description and that tab's
 * buttons on one line, status sub-tabs, an "N need a look" callout, then one
 * table with the row buttons in its last column. A tab with something
 * outstanding wears a red count. ?tab= picks the tab; the older ?returns=1 and
 * ?credit_requests=1 links (alert emails, the bell) still land on theirs.
 */
type TabKey = 'payouts' | 'returns' | 'applications' | 'remittances' | 'credits';
const TAB_KEYS: TabKey[] = ['payouts', 'returns', 'applications', 'remittances', 'credits'];
interface DriftResult {
  ok: true;
  checked: number;
  drifted: number;
  customers: Array<{
    customer_id: string;
    qbo_customer_id: number;
    name: string;
    differences: Array<{ field: string; portal: string; quickbooks: string }>;
  }>;
  missing_in_quickbooks: Array<{ customer_id: string; name: string; qbo_customer_id: number }>;
  no_billing_location: Array<{ customer_id: string; name: string }>;
}

export default function AdminPayments() {
  const [notice, setNotice] = useState<string | null>(null);

  // Sync from QuickBooks — pull changed invoices (payments made on OTHER
  // systems: checks keyed into QuickBooks, etc.) AND the customer list
  // (names, new customers, merges — otherwise nightly-only) into the mirror
  // NOW instead of waiting for the scheduled syncs.
  const [quickSyncing, setQuickSyncing] = useState(false);
  async function quickSyncInvoices() {
    setQuickSyncing(true);
    setNotice(null);
    try {
      await adminFetch('admin-sync-invoices', { method: 'POST' });
      setNotice('Sync complete — invoice paid/open statuses and the customer list are current with QuickBooks.');
    } catch (e) {
      setNotice(
        `Sync failed — the scheduled syncs will catch up. (${e instanceof Error ? e.message : 'error'})`,
      );
    } finally {
      setQuickSyncing(false);
    }
  }

  // Book settled payments — the manual twin of the hourly
  // stripe-settlement-sweep cron. ACH settles days after the charge and only
  // `payment_intent.succeeded` books it into QuickBooks, so when that webhook
  // is missed the money sits in Stripe with nothing on our side recording it.
  // Netlify won't let a scheduled function be called over HTTP, hence the
  // separate admin endpoint. Idempotent — pressing it twice can't double-book.
  const [booking, setBooking] = useState(false);
  async function bookSettledPayments() {
    setBooking(true);
    setNotice(null);
    try {
      const r = await adminFetch<{
        booked?: string[];
        scanned?: number;
        still_processing?: string[];
        needs_attention?: Array<{ payment_intent: string; stripe_status: string }>;
      }>('admin-stripe-settlement-sweep', { method: 'POST', body: JSON.stringify({}) });
      const booked = r.booked?.length ?? 0;
      const waiting = r.still_processing?.length ?? 0;
      const attention = r.needs_attention?.length ?? 0;
      setNotice(
        booked > 0
          ? `Booked ${booked} settled payment${booked === 1 ? '' : 's'} into QuickBooks — the customer gets a receipt and the invoice flips to paid.`
          : waiting > 0
            ? `Nothing to book — ${waiting} payment${waiting === 1 ? ' is' : 's are'} still settling at Stripe. They'll book automatically once they clear.`
            : 'Nothing to book — every Stripe payment is already recorded in QuickBooks.'
        + (attention > 0 ? ` ${attention} need${attention === 1 ? 's' : ''} a look: check the Returned payments tab.` : ''),
      );
    } catch (e) {
      setNotice(
        `Couldn't book settled payments — the hourly sweep will retry. (${e instanceof Error ? e.message : 'error'})`,
      );
    } finally {
      setBooking(false);
    }
  }

  // Customer drift — where the portal and QuickBooks disagree about the
  // customer master. The portal is the record and pushes changes out on save,
  // but a push is best-effort by design (it must never fail a save), so a
  // refusal or an outage leaves the two quietly apart. This is the watcher.
  // It REPORTS only: applying a difference is a decision about which side is
  // right, and that is not a machine's to make silently.
  const [checkingDrift, setCheckingDrift] = useState(false);
  const [drift, setDrift] = useState<DriftResult | null>(null);
  async function checkDrift() {
    setCheckingDrift(true);
    setNotice(null);
    setDrift(null);
    try {
      const r = await adminFetch<DriftResult>('admin-customer-drift', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setDrift(r);
      if (r.drifted === 0 && r.missing_in_quickbooks.length === 0) {
        setNotice(`Checked ${r.checked} customers — the portal and QuickBooks agree.`);
      }
    } catch (e) {
      setNotice(
        `Couldn't compare against QuickBooks. (${e instanceof Error ? e.message : 'error'})`,
      );
    } finally {
      setCheckingDrift(false);
    }
  }

  const [searchParams, setSearchParams] = useSearchParams();
  const asked = searchParams.get('tab') as TabKey | null;
  const tab: TabKey =
    asked && TAB_KEYS.includes(asked)
      ? asked
      : searchParams.get('returns') === '1'
        ? 'returns'
        : searchParams.get('credit_requests') === '1'
          ? 'credits'
          : 'payouts';
  const setTab = (k: TabKey) => setSearchParams({ tab: k }, { replace: true });

  // Each tab's outstanding count. Same queries the tabs use, so they share the cache.
  const payouts = useStripePayouts(true);
  const returns = usePaymentReturns();
  const tracking = usePaymentTracking();
  const remitNeeds = useRemittances('needs_review');
  const remitFailed = useRemittances('failed');
  const credits = useAdminCreditRequests('open');
  const tabs: Array<PayTab<TabKey>> = [
    { key: 'payouts', label: 'Stripe payouts & deposits', alert: (payouts.data?.payouts ?? []).filter(payoutNeedsLook).length },
    { key: 'returns', label: 'Returned payments', alert: (returns.data?.rows ?? []).filter((r) => returnView(r) === 'open').length },
    { key: 'applications', label: 'Payment applications', alert: unappliedNeedingLook(tracking.data?.accounts ?? []) },
    {
      key: 'remittances',
      label: 'Remittances',
      alert: (remitNeeds.data?.remittances.length ?? 0) + (remitFailed.data?.remittances.length ?? 0),
    },
    { key: 'credits', label: 'Credit requests', alert: credits.data?.requests.length ?? 0 },
  ];

  return (
    <div className="px-6 py-6 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-brix-ink flex items-center gap-2">
            Payments
            <InfoDot text="All customer payments run on Stripe. Sync from QuickBooks pulls changed invoices (e.g. checks keyed into QuickBooks) and the customer list (names, new customers, merges) into the mirror immediately; each job has its own tab below." />
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => void bookSettledPayments()} disabled={booking}>
            {booking ? <Loader2 size={14} className="animate-spin" /> : <Banknote size={14} />}
            {booking ? 'Booking…' : 'Book settled payments'}
          </Button>
          <InfoDot text="Bank (ACH) payments clear days after the customer pays, and only then can they be recorded in QuickBooks — normally that happens on its own within the hour. Press this to do it right now instead of waiting. Safe to press any time: it only touches payments that have actually cleared at Stripe, and never records one twice." />
          <Button variant="secondary" onClick={() => void quickSyncInvoices()} disabled={quickSyncing}>
            {quickSyncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {quickSyncing ? 'Syncing…' : 'Sync from QuickBooks'}
          </Button>
          <Button variant="secondary" onClick={() => void checkDrift()} disabled={checkingDrift}>
            {checkingDrift ? <Loader2 size={14} className="animate-spin" /> : <GitCompare size={14} />}
            {checkingDrift ? 'Comparing…' : 'Check customer drift'}
          </Button>
          <InfoDot text="Compares every active customer's master record — name, billing email, bill-to, terms, payment method, taxable — against what QuickBooks holds. The portal is the record and pushes changes out on save, so this catches the cases where a push was refused or QuickBooks was unreachable. It only reports; nothing is changed on either side." />
        </div>
      </div>

      {notice && (
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">{notice}</div>
      )}

      {drift && (drift.drifted > 0 || drift.missing_in_quickbooks.length > 0 || drift.no_billing_location.length > 0) && (
        <div className="rounded-xl border border-brix-border bg-brix-surface p-5 space-y-3">
          <div>
            <h2 className="text-base font-semibold text-brix-ink">
              Customer drift — {drift.drifted} of {drift.checked} differ from QuickBooks
            </h2>
            <p className="text-xs text-brix-muted mt-0.5 max-w-2xl">
              The portal is the record. Nothing here has been changed on either side — open the
              customer and re-save the field to push it, or fix it in QuickBooks if QuickBooks is
              the one that is right.
            </p>
          </div>

          {drift.missing_in_quickbooks.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 space-y-1">
              <p className="text-xs font-semibold text-red-800">
                {drift.missing_in_quickbooks.length} customer
                {drift.missing_in_quickbooks.length === 1 ? '' : 's'} not found in QuickBooks
              </p>
              <p className="text-[11px] text-red-700">
                Usually a merge or delete in QuickBooks. The portal keys on that id, so invoices
                for these accounts will stop matching.
              </p>
              {drift.missing_in_quickbooks.map((m) => (
                <Link
                  key={m.customer_id}
                  to={`/admin/customers/${m.customer_id}`}
                  className="block text-[11px] text-red-800 underline underline-offset-2"
                >
                  {m.name} (QBO #{m.qbo_customer_id})
                </Link>
              ))}
            </div>
          )}

          {drift.customers.length > 0 && (
            <div className="divide-y divide-brix-border overflow-hidden rounded-lg border border-brix-border">
              {drift.customers.map((c) => (
                <div key={c.customer_id} className="px-3 py-2.5 bg-brix-surface">
                  <Link
                    to={`/admin/customers/${c.customer_id}`}
                    className="text-xs font-semibold text-brix-primary underline underline-offset-2"
                  >
                    {c.name}
                  </Link>
                  <div className="mt-1 space-y-0.5">
                    {c.differences.map((d) => (
                      <p key={d.field} className="text-[11px] text-brix-muted">
                        <span className="font-semibold text-brix-ink">{d.field}</span> — portal:{' '}
                        <span className="text-brix-ink">{d.portal}</span> · QuickBooks:{' '}
                        <span className="text-brix-ink">{d.quickbooks}</span>
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {drift.no_billing_location.length > 0 && (
            <p className="text-[11px] text-amber-700">
              {drift.no_billing_location.length} customer
              {drift.no_billing_location.length === 1 ? ' has' : 's have'} no bill-to marked —
              invoices for them carry no address. Pick one on each customer&rsquo;s Locations tab.
            </p>
          )}
        </div>
      )}

      <div className="space-y-4">
        <PayTabs tabs={tabs} value={tab} onChange={setTab} />
        <div className="rounded-xl border border-brix-border bg-brix-surface p-5">
          {tab === 'payouts' && <StripePayoutsPanel />}
          {tab === 'returns' && <ReturnsPanel />}
          {tab === 'applications' && <PaymentTrackingPanel />}
          {tab === 'remittances' && <RemittancesPanel />}
          {tab === 'credits' && <CreditRequestsPanel />}
        </div>
      </div>
    </div>
  );
}
